import type { WorkItem } from './workflow'

// Derived, render-only signals for a board card. Pure functions over the work
// item + a reference time (passed in, never read from the clock here) so they
// stay testable and free of side effects.

type DueUrgency = 'overdue' | 'soon' | 'normal'

const MS_PER_DAY = 1000 * 60 * 60 * 24
const SOON_DAYS = 3

// Whole days from `from` to `to` (negative if `to` is before `from`).
const daysBetween = (from: number, to: number): number => Math.floor((to - from) / MS_PER_DAY)

const dueUrgency = (dueDate: string | null, nowMs: number): DueUrgency => {
	if (!dueDate) return 'normal'
	const due = new Date(dueDate).getTime()
	if (Number.isNaN(due)) return 'normal'
	const days = daysBetween(nowMs, due)
	if (days < 0) return 'overdue'
	if (days <= SOON_DAYS) return 'soon'
	return 'normal'
}

// Timestamp the item entered its current step, read from the phase ledger's
// open visit; falls back to creation for pre-ledger items. Used to surface
// stuck-time.
const enteredCurrentStepAt = (item: WorkItem): string | null => {
	const entry = item.phaseLedger.at(-1)
	if (entry && entry.phaseId === item.currentPhaseId && entry.leftAt === null) {
		const visit = entry.steps.at(-1)
		if (visit && visit.stepId === item.currentStepId && visit.leftAt === null) {
			return visit.enteredAt
		}
		return entry.enteredAt
	}
	return item.createdAt
}

// Days the item has sat in its current step. null when unknown.
const stepAgeDays = (item: WorkItem, nowMs: number): number | null => {
	const enteredAt = enteredCurrentStepAt(item)
	if (!enteredAt) return null
	const entered = new Date(enteredAt).getTime()
	if (Number.isNaN(entered)) return null
	return Math.max(0, daysBetween(entered, nowMs))
}

export type { DueUrgency }
export { dueUrgency, enteredCurrentStepAt, stepAgeDays }
